export const darkColors = {
  bgBase: '#2A2C31', // app background (lightest dark — canvas)
  bgElevated: '#1E1F22', // cards, modals, table (darker — recessed)
  bgSunken: '#161B22', // table header / deepest
  bgControl: '#21262D', // inputs/selects

  // === Surface hierarchy (for depth) ===
  surfaceSecondary: '#1E1F22', // report panel, secondary surfaces (matches elevated)
  surfaceTertiary: '#21262D',

  // === Shell tokens (Header + Left Sidebar unified frame) ===
  shellBg: '#0B223A',
  shellTextPrimary: 'rgba(255, 255, 255, 0.92)',
  shellTextMuted: 'rgba(255, 255, 255, 0.70)',
  shellBorder: 'rgba(255, 255, 255, 0.10)',
  shellHover: 'rgba(255, 255, 255, 0.12)',
  shellSelected: 'rgba(0, 174, 239, 0.22)',
  shellText: 'rgba(255, 255, 255, 0.70)',
  shellTextHover: 'rgba(255, 255, 255, 0.92)',
  shellTextSelected: 'rgba(255, 255, 255, 0.92)',

  // === Sub-sidebar tokens (worklists, filters) ===
  subSidebarBg: '#04162A',
  subSidebarBorder: 'rgba(255, 255, 255, 0.10)',
  subSidebarBorderSubtle: 'rgba(255, 255, 255, 0.06)',
  subSidebarTextPrimary: 'rgba(255, 255, 255, 0.92)',
  subSidebarTextSecondary: 'rgba(255, 255, 255, 0.70)',
  subSidebarTextTertiary: 'rgba(255, 255, 255, 0.50)',
  subSidebarHover: 'rgba(255, 255, 255, 0.06)',
  subSidebarSelected: 'rgba(0, 174, 239, 0.15)',
  subSidebarSurface1: '#091e37', // HSL(212, 83%, 15%) — controls at rest
  subSidebarSurface2: '#093362', // HSL(212, 83%, 21%) — hover / focus

  // === Viewer tokens (DICOM viewer area) ===
  viewerBg: '#0A0A0A',
  filmstripBg: '#071A2D',

  text: '#E6EDF3', // primary text (~14:1 on bgBase)
  textSec: '#8B949E', // secondary text (~5:1 on bgBase)
  textTertiary: '#6E7681', // tertiary/disabled (~3.5:1)
  textDisabled: '#6E7681', // alias for compatibility

  border: '#3A3D42', // dividers (better visibility)
  borderSubtle: '#2F3238', // very subtle borders
  hover: '#2F3238', // row/btn hover
  selected: '#363A40', // selected row/cell

  primary: '#00AEEF', // brand accent
  primaryHover: '#33C1F3', // lighter on hover
  primaryActive: '#0095CC', // darker on active

  // === Status colors (updated to muted tones) ===
  success: '#069279',
  warning: '#fe8888',
  error: '#a42128',
  info: '#1998e9',

  // === Priority colors (clinical safety) ===
  priorityStat: '#F84F31',
  priorityStatBg: 'rgba(248, 79, 49, 0.15)',
  priorityUrgent: '#D4A017',
  priorityUrgentBg: 'rgba(212, 160, 23, 0.15)',
  priorityRoutine: '#66D4FF',
  priorityRoutineBg: 'rgba(0, 174, 239, 0.12)',

  // === AI accent (updated to green tones) ===
  aiAccent: '#045e43',
  aiGlow: 'rgba(4, 94, 67, 0.4)',
  accentGradient: 'linear-gradient(135deg, #00AEEF 0%, #045e43 100%)',
  findingBg: 'rgba(0, 174, 239, 0.08)',

  // === Viewer annotation tokens (viewer canvas is always dark) ===
  annotationAiActive: '#5FD3A6',
  annotationAiInactive: 'rgba(95, 211, 166, 0.5)',
  annotationManualActive: '#1998E9',
  annotationManualInactive: 'rgba(25, 152, 233, 0.5)',
  annotationChipBg: '#1E1F22',
  annotationChipText: '#E6EDF3',
  annotationChipBorder: 'rgba(255, 255, 255, 0.15)',
  annotationChipTextActive: '#0A1F17',

  // === Glassmorphism ===
  glassBg: 'rgba(42, 44, 49, 0.92)',
  glassBorder: 'rgba(58, 61, 66, 0.6)',
  glassShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',

  // === Shadows (simplified per new spec) ===
  shadowSubtle: '0 1px 3px rgba(0, 0, 0, 0.4)',
  shadowCard: '0 2px 8px rgba(0, 0, 0, 0.5)',
  shadowElevated: '0 4px 16px rgba(0, 0, 0, 0.6)',
  shadowButtonPrimary: '0 0 0 3px rgba(0, 174, 239, 0.3)',
  shadowFocus: '0 0 0 3px rgba(0, 174, 239, 0.3)',
  shadowBlueGlow:
    '0 0 0 3px rgba(0, 174, 239, 0.3), 0 0 12px rgba(0, 174, 239, 0.4)',
  shadowButton:
    '0 2px 8px rgba(0, 174, 239, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)',
};

export const lightColors = {
  bgBase: '#E8EDEF', // main canvas/body background - cool blue-tinted grey
  bgElevated: '#F4F5F7', // cards, elevated surfaces
  bgSunken: '#CFD3D9', // table headers, toolbars
  bgControl: '#FFFFFF', // inputs, white elements

  surfaceSecondary: '#F4F5F7', // report panel, secondary surfaces
  surfaceTertiary: '#FAFBFC', // alternate row background

  // === Shell tokens (Header + Left Sidebar unified frame) ===
  shellBg: '#1B4468',
  shellTextPrimary: 'rgba(255, 255, 255, 0.95)',
  shellTextMuted: 'rgba(255, 255, 255, 0.75)',
  shellBorder: 'rgba(255, 255, 255, 0.12)',
  shellHover: 'rgba(255, 255, 255, 0.12)',
  shellSelected: 'rgba(255, 255, 255, 0.18)',
  shellText: 'rgba(255, 255, 255, 0.75)',
  shellTextHover: 'rgba(255, 255, 255, 0.95)',
  shellTextSelected: 'rgba(255, 255, 255, 0.95)',

  // === Sub-sidebar tokens (worklists, filters) ===
  subSidebarBg: '#B8D4ED',
  subSidebarBorder: '#9ECFEA',
  subSidebarBorderSubtle: 'rgba(0, 15, 42, 0.08)',
  subSidebarTextPrimary: '#0F172A',
  subSidebarTextSecondary: '#334155',
  subSidebarTextTertiary: '#475569',
  subSidebarHover: 'rgba(255, 255, 255, 0.40)',
  subSidebarSelected: 'rgba(255, 255, 255, 0.50)',
  subSidebarSurface1: '#D2E4F4', // HSL(208, 59%, 89%) — controls at rest
  subSidebarSurface2: '#EBF3FA', // HSL(208, 59%, 95%) — hover / focus

  // === Viewer tokens (DICOM viewer area - always dark) ===
  viewerBg: '#0A0A0A',
  filmstripBg: '#071A2D',

  text: '#0F172A', // primary text
  textSec: '#64748B', // secondary/label text
  textTertiary: '#94A3B8', // tertiary/placeholder text
  textDisabled: '#CBD5E1', // disabled text

  border: '#CBD5E1', // default borders
  borderSubtle: '#E2E8F0', // subtle borders
  hover: '#FFFFFF', // row/btn hover with shadow
  selected: '#DBEAFE', // selected row/cell (light blue)

  // === Primary (updated to cyan) ===
  primary: '#1998e9',
  primaryHover: '#1D4ED8', // darker on hover
  primaryActive: '#1E40AF', // even darker on active

  // === Status colors (updated to muted tones) ===
  success: '#069279',
  warning: '#fe8888',
  error: '#a42128',
  info: '#1998e9',

  // === Priority colors (clinical safety) ===
  priorityStat: '#DC2626',
  priorityStatBg: 'rgba(220, 38, 38, 0.10)',
  priorityUrgent: '#B8860B',
  priorityUrgentBg: 'rgba(184, 134, 11, 0.10)',
  priorityRoutine: '#00AEEF',
  priorityRoutineBg: 'rgba(0, 174, 239, 0.10)',

  // === AI accent (updated to green tones) ===
  aiAccent: '#689E8E',
  aiGlow: 'rgba(104, 158, 142, 0.3)',
  accentGradient: 'linear-gradient(135deg, #1998e9 0%, #689E8E 100%)',
  findingBg: 'rgba(25, 152, 233, 0.06)',

  // === Viewer annotation tokens (viewer canvas is always dark) ===
  annotationAiActive: '#5FD3A6',
  annotationAiInactive: 'rgba(95, 211, 166, 0.5)',
  annotationManualActive: '#1998E9',
  annotationManualInactive: 'rgba(25, 152, 233, 0.5)',
  annotationChipBg: '#1E1F22',
  annotationChipText: '#E6EDF3',
  annotationChipBorder: 'rgba(255, 255, 255, 0.15)',
  annotationChipTextActive: '#0A1F17',

  glassBg: 'rgba(255, 255, 255, 0.85)',
  glassBorder: 'rgba(203, 213, 225, 0.5)',
  glassShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',

  // === Shadows (updated per new spec) ===
  shadowSubtle: '0 1px 3px rgba(0, 0, 0, 0.08)',
  shadowCard: '0 2px 4px rgba(0, 0, 0, 0.12)',
  shadowElevated: '0 4px 16px rgba(0, 0, 0, 0.15)',
  shadowButtonPrimary: '0 2px 8px rgba(37, 99, 235, 0.15)',
  shadowFocus: '0 0 0 3px rgba(37, 99, 235, 0.1)',
  shadowBlueGlow:
    '0 0 0 3px rgba(37, 99, 235, 0.2), 0 0 12px rgba(37, 99, 235, 0.3)',
  shadowButton:
    '0 2px 8px rgba(37, 99, 235, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)',
};
